/** Path helpers, transcribed from the reference field. */

export const arcPath = (cx: number, cy: number, r: number, a0: number, a1: number) => {
  const at = (a: number): [number, number] => [
    cx + r * Math.cos((a * Math.PI) / 180),
    cy + r * Math.sin((a * Math.PI) / 180),
  ];
  const [x0, y0] = at(a0);
  const [x1, y1] = at(a1);
  return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${a1 - a0 > 180 ? 1 : 0} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
};

/** A waxing moon: `f` of the disc lit, drawn as a phase, never a pie. */
export const phasePath = (s: number, f: number) => {
  const rx = Math.abs(1 - 2 * f) * s;
  const sweep = f < 0.5 ? 1 : 0;
  return `M 0 ${-s} A ${s} ${s} 0 0 0 0 ${s} A ${rx.toFixed(2)} ${s} 0 0 ${sweep} 0 ${-s} Z`;
};

/** A gentle trail through claim nodes — a Catmull-Rom read as beziers. */
export const trailPath = (pts: [number, number][]) => {
  if (pts.length < 2) return "";
  const P = [pts[0], ...pts, pts[pts.length - 1]];
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 1; i < P.length - 2; i += 1) {
    const [x0, y0] = P[i - 1];
    const [x1, y1] = P[i];
    const [x2, y2] = P[i + 1];
    const [x3, y3] = P[i + 2];
    d +=
      ` C ${(x1 + (x2 - x0) / 6).toFixed(2)} ${(y1 + (y2 - y0) / 6).toFixed(2)}` +
      ` ${(x2 - (x3 - x1) / 6).toFixed(2)} ${(y2 - (y3 - y1) / 6).toFixed(2)} ${x2} ${y2}`;
  }
  return d;
};

/** Offset a polyline sideways, so one road can be walked in two lanes. */
export const laneOffset = (pts: [number, number][], d: number): [number, number][] =>
  pts.map((pt, i) => {
    const a = pts[Math.max(0, i - 1)];
    const b = pts[Math.min(pts.length - 1, i + 1)];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const L = Math.hypot(dx, dy) || 1;
    return [pt[0] + (-dy / L) * d, pt[1] + (dx / L) * d];
  });

/**
 * Where a planet's claims sit inside it.
 *
 * The reference hand-authored a polyline per claim count (1..6).  A
 * registry planet can carry any number, so counts past the authored ones
 * fall back to a generated serpentine with the same amplitude and
 * spacing — the drawn line stays a trail rather than becoming a list.
 */
const AUTHORED: Record<number, [number, number][]> = {
  1: [[0, 0]],
  2: [[-19, 8], [19, -8]],
  3: [[-25, 9], [0, -10], [25, 7]],
  4: [[-27, 8], [-9, -11], [9, 9], [27, -9]],
  5: [[-28, 7], [-14, -11], [0, 8], [14, -11], [28, 6]],
  6: [[-29, 8], [-17, -10], [-6, 8], [6, -10], [17, 8], [29, -10]],
};

export function claimTrail(count: number): [number, number][] {
  if (count <= 0) return [];
  const authored = AUTHORED[count];
  if (authored) return authored;
  const span = 60;
  return [...Array(count)].map((_, i) => {
    const t = count === 1 ? 0.5 : i / (count - 1);
    return [-span / 2 + span * t, i % 2 ? -10 : 8] as [number, number];
  });
}
